import { useState } from 'react';
import { Icons, cx } from '../../components/ui.tsx';
import { isDriveDrag, readDrag, type DragPayload } from './dragging.ts';
import type { Breadcrumb } from '../../lib/types.ts';

/**
 * Breadcrumb trail. The last item is the current folder and is not a link —
 * a control that navigates to where you already are is noise.
 *
 * Every ancestor is also a drop target, which is how something gets back
 * *out* of a folder. Rows can be dragged onto folders, but a folder's own
 * contents have nowhere to go otherwise: there is no row representing "up".
 */
export function Breadcrumbs({
  items,
  onNavigate,
  onDropInto,
}: {
  items: Breadcrumb[];
  onNavigate: (id: string | null) => void;
  /** Move a dragged row into this ancestor. Omitted where dropping is not offered. */
  onDropInto?: ((target: string | null, payload: DragPayload) => void) | undefined;
}) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Crumb
            label="My Drive"
            target={null}
            current={items.length === 0}
            onNavigate={onNavigate}
            {...(onDropInto ? { onDropInto } : {})}
          />
        </li>

        {items.map((crumb, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={crumb.id} className="flex items-center gap-1">
              <span className="text-subtle" aria-hidden>
                <Icons.ChevronRight />
              </span>
              {isLast ? (
                <span className="px-2 py-1 text-body-sm font-semibold text-ink" aria-current="page">
                  {crumb.name}
                </span>
              ) : (
                <Crumb
                  label={crumb.name}
                  target={crumb.id}
                  current={false}
                  onNavigate={onNavigate}
                  {...(onDropInto ? { onDropInto } : {})}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function Crumb({
  label,
  target,
  current,
  onNavigate,
  onDropInto,
}: {
  label: string;
  target: string | null;
  current: boolean;
  onNavigate: (id: string | null) => void;
  onDropInto?: ((target: string | null, payload: DragPayload) => void) | undefined;
}) {
  const [over, setOver] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onNavigate(target)}
      aria-current={current ? 'page' : undefined}
      onDragOver={(event) => {
        if (!onDropInto || !isDriveDrag(event)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        if (!onDropInto || !isDriveDrag(event)) return;
        event.preventDefault();
        event.stopPropagation();
        setOver(false);
        const payload = readDrag(event);
        if (payload) onDropInto(target, payload);
      }}
      className={cx(
        'rounded-md px-2 py-1 text-body-sm transition-fast hover:bg-blue-50',
        current ? 'font-semibold text-ink' : 'text-blue-700',
        over && 'bg-blue-100 outline outline-2 -outline-offset-2 outline-blue-600',
      )}
    >
      {label}
    </button>
  );
}
